import {
	Alert,
	Box,
	IconButton,
	Link,
	List,
	ListItem,
	Popover,
	Tooltip,
	Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LINK_SYNC_FIELDS } from './linkSyncFields';

interface Props {
	/** Which record this page shows; the banner names the other one. */
	linkedTo: 'member' | 'familyMember';
	fileNo?: string | null;
	/** Detail page of the other record; `undefined` shows the File no. as text. */
	href?: string;
}

/** DMS-REQ-WEB-CHA-005 §七: the record is linked to the other side as the same person. */
export default function LinkedRecordBanner({ linkedTo, fileNo, href }: Props) {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	return (
		<>
			<Alert
				severity='info'
				sx={{ marginY: 2, alignItems: 'center' }}
				action={
					<Tooltip title={t('identityLink.syncedFieldsTitle')}>
						<IconButton
							size='small'
							color='inherit'
							onClick={(e) => setAnchorEl(e.currentTarget)}
						>
							<InfoOutlinedIcon fontSize='small' />
						</IconButton>
					</Tooltip>
				}
			>
				<Trans
					i18nKey={
						linkedTo === 'member'
							? 'identityLink.linkedToMember'
							: 'identityLink.linkedToFamilyMember'
					}
					values={{ fileNo: fileNo || '-' }}
					components={{
						anchor: href ? (
							<Link
								href={href}
								target='_blank'
								rel='noopener noreferrer'
								color='#1BBED7'
							/>
						) : (
							<span />
						),
					}}
				/>
			</Alert>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Box sx={{ padding: 2, maxWidth: 480 }}>
					<Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
						{t('identityLink.syncedFieldsTitle')}
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						{t('identityLink.syncedFieldsText')}
					</Typography>
					<List
						dense
						sx={{
							columnCount: 2,
							listStyleType: 'disc',
							paddingLeft: 2,
						}}
					>
						{LINK_SYNC_FIELDS.map((field) => (
							<ListItem
								key={field.key}
								sx={{ display: 'list-item', paddingLeft: 0 }}
							>
								<Typography variant='body2'>{t(field.label)}</Typography>
							</ListItem>
						))}
					</List>
				</Box>
			</Popover>
		</>
	);
}
